// Genotype matching for the condition catalog.
//
// Data lives in catalog.ts, types in conditions-types.ts. Both are re-exported
// here so callers only ever import from './lib/conditions'.

// ── STRAND ───────────────────────────────────────────────────────────────────
// `alleles` are given on the dbSNP/forward orientation. Consumer exports vary,
// so `matchMarker` detects a strand flip by comparing the observed call to both
// the listed pair and its complement.
//
// Palindromic SNPs (A/T, C/G) read identically on both strands, so a flip is
// undetectable — but only *homozygous* calls are actually ambiguous. A
// heterozygous call carries one of each allele regardless of orientation, so it
// still resolves to exactly one copy. Homozygous palindromic calls therefore get
// their own status and report both possible counts rather than picking one.

import type { ParsedGenome, Variant } from '../types';
import type { Condition, ConditionMarker } from './conditions-types';
import { CATEGORY_LABELS } from './conditions-types';
import { CONDITIONS } from './catalog';

export * from './conditions-types';
export { CONDITIONS } from './catalog';

// ─────────────────────────────────────────────────────────────────────────────
// Genotype matching
// ─────────────────────────────────────────────────────────────────────────────

const COMPLEMENT: Record<string, string> = { A: 'T', T: 'A', C: 'G', G: 'C' };

function complement(base: string): string {
  return COMPLEMENT[base] ?? base;
}

/**
 * A/T and C/G SNPs read the same on either strand, so an export that used the
 * opposite strand is indistinguishable from one that didn't.
 */
export function isPalindromic([a, b]: [string, string]): boolean {
  return complement(a) === b;
}

export type MatchStatus =
  /** rsID present, genotype readable, orientation resolved. */
  | 'called'
  /**
   * A homozygous call on a palindromic (A/T or C/G) SNP. The call reads
   * identically on both strands, so the copy count is genuinely unknowable
   * from this file alone: it is either `effectCopies` or `alternateCopies`,
   * and nothing here can distinguish them.
   */
  | 'strand-ambiguous'
  /** rsID present but the call is a no-call ("--", "..", "00", "NN"). */
  | 'no-call'
  /** rsID present but the genotype isn't two plain bases (e.g. an indel). */
  | 'unreadable'
  /** The alleles don't fit the SNP on either strand — likely a different variant. */
  | 'mismatch'
  /** rsID not in this file at all. */
  | 'absent';

export interface MarkerMatch {
  marker: ConditionMarker;
  status: MatchStatus;
  /** The raw call from the file, when present. */
  genotype: string | null;
  variant: Variant | null;
  /** Copies of the effect allele (0–2) reading the file as forward-strand. */
  effectCopies: number | null;
  /**
   * Only set for 'strand-ambiguous': the count you would get reading the same
   * call as reverse-strand. Always 2 − effectCopies.
   */
  alternateCopies: number | null;
  /** True when the call had to be complemented to fit the listed alleles. */
  strandFlipped: boolean;
  /** True only where orientation genuinely cannot be recovered. */
  strandAmbiguous: boolean;
}

export interface ConditionMatch {
  condition: Condition;
  markers: MarkerMatch[];
  /** Markers with a definite copy count (excludes strand-ambiguous). */
  covered: number;
  /** Markers present in the file at all, called or not. */
  present: number;
  /** Markers where the person carries ≥1 copy of a risk-direction allele. */
  riskHits: number;
  /** Markers where the person carries ≥1 copy of a protective-direction allele. */
  protectiveHits: number;
  /** Markers whose count could not be resolved because of strand ambiguity. */
  ambiguous: number;
}

function readBases(genotype: string): [string, string] | null {
  const g = genotype.trim().toUpperCase();
  if (g.length !== 2) return null;
  const [a, b] = [g[0], g[1]];
  if (!(a in COMPLEMENT) || !(b in COMPLEMENT)) return null;
  return [a, b];
}

/**
 * Placeholder characters used across export formats for "no result here".
 * A single one poisons the whole call — "A0" and "AN" are half-reads, not
 * genotypes, and must not be counted as if the missing allele were reference.
 */
const NO_CALL_CHARS = new Set(['-', '.', '0', 'N', '?']);

function isNoCall(genotype: string): boolean {
  const g = genotype.trim().toUpperCase();
  if (!g) return true;
  for (const ch of g) if (NO_CALL_CHARS.has(ch)) return true;
  return false;
}

/** A record that actually tells us something, as opposed to a placeholder. */
function isInformative(v: Variant): boolean {
  return !isNoCall(v.genotype) && readBases(v.genotype) !== null;
}

/**
 * Fast rsID → Variant index.
 *
 * Duplicate rsIDs are real: probe duplicates and pseudoautosomal-region lines
 * show up in 23andMe/Ancestry exports, and one rsID can span several VCF rows.
 * First-wins would let a leading no-call mask a real genotype further down the
 * file, so an informative record always beats a placeholder.
 */
export function buildVariantIndex(genome: ParsedGenome): Map<string, Variant> {
  const index = new Map<string, Variant>();
  for (const v of genome.variants) {
    const key = v.rsid.toLowerCase();
    if (!key || key === '.') continue;
    const existing = index.get(key);
    if (!existing) {
      index.set(key, v);
      continue;
    }
    if (!isInformative(existing) && isInformative(v)) index.set(key, v);
  }
  return index;
}

export function matchMarker(
  marker: ConditionMarker,
  index: Map<string, Variant>,
): MarkerMatch {
  const base: MarkerMatch = {
    marker,
    status: 'absent',
    genotype: null,
    variant: null,
    effectCopies: null,
    alternateCopies: null,
    strandFlipped: false,
    strandAmbiguous: false,
  };

  const variant = index.get(marker.rsid.toLowerCase());
  if (!variant) return base;

  base.variant = variant;
  base.genotype = variant.genotype;

  if (isNoCall(variant.genotype)) return { ...base, status: 'no-call' };

  const bases = readBases(variant.genotype);
  if (!bases) return { ...base, status: 'unreadable' };

  const expected = new Set(marker.alleles);
  const flipped = new Set(marker.alleles.map(complement));
  const fitsExpected = bases.every((b) => expected.has(b));
  const fitsFlipped = bases.every((b) => flipped.has(b));

  if (!fitsExpected && !fitsFlipped) return { ...base, status: 'mismatch' };

  const palindromic = isPalindromic(marker.alleles);
  const heterozygous = bases[0] !== bases[1];

  // A heterozygous call carries one of each allele whichever strand it was
  // written on, so even a palindromic SNP resolves to exactly one copy. Only
  // homozygous palindromic calls are genuinely undecidable.
  if (palindromic && !heterozygous) {
    const forward = bases.filter((b) => b === marker.effectAllele).length;
    return {
      ...base,
      status: 'strand-ambiguous',
      effectCopies: forward,
      alternateCopies: 2 - forward,
      strandAmbiguous: true,
    };
  }

  const useFlipped = !palindromic && !fitsExpected && fitsFlipped;
  const normalized = useFlipped ? bases.map(complement) : bases;
  const effectCopies = normalized.filter((b) => b === marker.effectAllele).length;

  return {
    ...base,
    status: 'called',
    effectCopies,
    strandFlipped: useFlipped,
  };
}

export function matchCondition(
  condition: Condition,
  index: Map<string, Variant>,
): ConditionMatch {
  const markers = condition.markers.map((m) => matchMarker(m, index));
  let riskHits = 0;
  let protectiveHits = 0;
  for (const m of markers) {
    // Strand-ambiguous markers are deliberately excluded: counting a coin-flip
    // as a hit would put an unearned number in the card headline.
    if (m.status !== 'called' || !m.effectCopies) continue;
    if (m.marker.direction === 'risk') riskHits++;
    else if (m.marker.direction === 'protective') protectiveHits++;
  }
  return {
    condition,
    markers,
    covered: markers.filter((m) => m.status === 'called').length,
    present: markers.filter((m) => m.status !== 'absent').length,
    ambiguous: markers.filter((m) => m.status === 'strand-ambiguous').length,
    riskHits,
    protectiveHits,
  };
}

/** Every rsID the catalog knows about — used to seed the demo genome. */
export function catalogRsids(): string[] {
  const seen = new Set<string>();
  for (const c of CONDITIONS) for (const m of c.markers) seen.add(m.rsid);
  return [...seen];
}

/**
 * Free-text search over condition name, aliases, category, gene, and rsID.
 * Ranked so name matches beat alias matches beat gene/rsID matches.
 */
export function searchConditions(query: string): Condition[] {
  const q = query.trim().toLowerCase();
  if (!q) return CONDITIONS;

  const scored: { condition: Condition; score: number }[] = [];
  for (const condition of CONDITIONS) {
    const name = condition.name.toLowerCase();
    let score = 0;

    if (name === q) score = 100;
    else if (name.startsWith(q)) score = 80;
    else if (name.includes(q)) score = 60;

    if (!score) {
      for (const alias of condition.aliases) {
        const a = alias.toLowerCase();
        if (a === q) { score = Math.max(score, 70); break; }
        if (a.includes(q)) score = Math.max(score, 45);
      }
    }
    if (!score && CATEGORY_LABELS[condition.category].toLowerCase().includes(q)) score = 30;
    if (!score) {
      for (const m of condition.markers) {
        if (m.rsid.toLowerCase() === q) { score = Math.max(score, 90); break; }
        if (m.gene.toLowerCase().includes(q)) score = Math.max(score, 40);
      }
    }
    if (!score && condition.summary.toLowerCase().includes(q)) score = 15;

    if (score) scored.push({ condition, score });
  }

  return scored
    .sort((a, b) => b.score - a.score || a.condition.name.localeCompare(b.condition.name))
    .map((s) => s.condition);
}
